import { MoreVerticalIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ErrorMessage } from "@/app/components/error-message";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useOrganisation } from "@/features/select-organisation/api/get-my-organisation";
import { PublicInvitation, WithPagination } from "@classview/shared-types";
import { useQueryClient } from "@tanstack/react-query";

import { useDeleteInvitation } from "../api/delete-invitation";

type InvitationRowActionsProps = {
  invitationId: string;
};

export function InvitationRowActions(props: InvitationRowActionsProps) {
  const { organisation } = useOrganisation();
  const {
    mutateAsync: deleteInvitation,
    isPending,
    isSuccess,
    isError,
    error,
    reset,
  } = useDeleteInvitation({
    onSuccess: () => {
      queryClient.setQueriesData(
        { queryKey: ["invitations", organisation.id], exact: false },
        (oldData: WithPagination<PublicInvitation> | undefined) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            items: oldData.items.filter(
              (invitation) => invitation.id !== props.invitationId,
            ),
          };
        },
      );
      navigate("/settings/invitations");
    },
  });
  const [isDeleting, setIsDeleting] = useState(false);

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isDeleting) {
      reset();
    }
  }, [isDeleting, reset]);

  const handleDelete = async () => {
    try {
      await deleteInvitation({ invitationId: props.invitationId });
      queryClient.invalidateQueries({ queryKey: ["invitations"] });
      setIsDeleting(false);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Dialog onOpenChange={setIsDeleting} open={isDeleting}>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon">
            <MoreVerticalIcon />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {/* <DropdownMenuItem onClick={handleEdit}>Edit</DropdownMenuItem> */}

          <DialogTrigger asChild>
            <DropdownMenuItem className="text-destructive">
              Delete
            </DropdownMenuItem>
          </DialogTrigger>
        </DropdownMenuContent>
      </DropdownMenu>

      <DialogContent>
        <DialogHeader>
          <DialogTitle>Are you absolutely sure?</DialogTitle>
          <DialogDescription>
            This action cannot be undone. Are you sure you want to permanently
            delete this invitation?
          </DialogDescription>
        </DialogHeader>
        {isError && <ErrorMessage error={error} />}
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" disabled={isPending}>
              Cancel
            </Button>
          </DialogClose>
          <Button
            type="submit"
            variant="destructive"
            onClick={handleDelete}
            disabled={isPending || isSuccess}
            isLoading={isPending || isSuccess}
            isAnimated
          >
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
